/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { createContext, useState, useContext, useRef, useEffect, useCallback } from "react";
import styled, { css } from "styled-components";
import Button from "./Button";
import Icon from "./Icon";
import ButtonGroup from "./ButtonGroup";
import Text from "./Text";
import { useTranslate } from "../../hooks/useTranslate";
import { useClickableA11y } from "../../hooks/a11y/useClickableA11y";
import { focusRingInsetStyles } from "../GlobalWidgetStyle";

const StyledChipsItem = styled.div`
  flex-shrink: 0;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.8);
  color: rgba(${(props) => props.theme.colors.text.rgba});
  background: rgba(${(props) => props.theme.colors.accent.rgb}, 0.15);
  cursor: pointer;
  transition: background 0.2s;

  &:focus-visible {
    ${focusRingInsetStyles}
  }

  @media (hover: hover) {
    &:hover {
      color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
      background: rgba(${({ theme }) => theme.colors.accent.rgba});
    }
  }

  ${(props) =>
    props.$active &&
    css`
      color: rgba(${props.theme.colors.accentContrast.rgba});
      background: rgba(${props.theme.colors.accent.rgba});
    `}

  ${({ theme }) => theme.containerMedia.sm} {
    font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.75);
  }
`;

const StyledChipsList = styled.div`
  position: sticky;
  top: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, auto));
  gap: 4px;
  background: rgba(${({ theme }) => theme.colors.background.rgba});
  z-index: 1;

  ${(props) => props.$isSticky && `box-shadow: 0 4px 2px -2px rgba(${props.theme.colors.text.rgb}, 0.1);`}
`;

const ChipsScrollable = styled.div`
  display: flex;
  gap: 8px;
  padding-block: 4px;
  overflow: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  transition: ease 0.2s;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledChipsSection = styled.div`
  display: grid;
  gap: 8px;
`;

const StyledChipsButtons = styled((props) => <ButtonGroup {...props} />)`
  margin-left: auto;
`;

const ChipsContext = createContext();

const SCROLL_BY_AMOUNT = 200;
const SCROLL_TIMEOUT = 1000;

const Chips = ({ children, defaultChipId }) => {
  const [activeChip, setActiveChip] = useState(defaultChipId || null);
  const [scrollInitiatedByClick, setScrollInitiatedByClick] = useState(false);
  const [chipsListScrollLeft, setChipsListScrollLeft] = useState(0);

  const sectionRefs = useRef(new Map());
  const chipsListRef = useRef(null);
  const chipsRef = useRef(null);

  const scrollToSection = useCallback((id) => {
    const ref = sectionRefs.current.get(id);
    if (ref?.current && chipsRef.current) {
      const chipsHeight = chipsRef.current.getBoundingClientRect().height;
      const isSticky = chipsRef.current.getBoundingClientRect().top === 0;

      const sectionTop = ref.current.getBoundingClientRect().top + window.scrollY;
      const targetScrollTop = sectionTop - (isSticky ? chipsHeight : 0);

      window.scrollTo({
        top: targetScrollTop,
        behavior: "smooth",
      });

      setTimeout(() => setScrollInitiatedByClick(false), SCROLL_TIMEOUT);
    }
  }, []);

  const handleChipClick = useCallback(
    (id) => {
      setActiveChip(id);
      setScrollInitiatedByClick(true);
      scrollToSection(id);
    },
    [scrollToSection]
  );

  const registerSection = useCallback((id, ref) => {
    sectionRefs.current.set(id, ref);
  }, []);

  const scrollToChip = useCallback((id) => {
    if (chipsListRef.current) {
      const chipElement = chipsListRef.current.querySelector(`[data-chip-id="${id}"]`);

      if (chipElement) {
        const containerWidth = chipsListRef.current.clientWidth;
        const containerLeft = chipsListRef.current.getBoundingClientRect().left;

        const chipRect = chipElement.getBoundingClientRect();
        const chipLeft = chipRect.left - containerLeft;
        const chipWidth = chipRect.width;

        let scrollPosition = chipsListRef.current.scrollLeft + chipLeft;

        if (chipLeft + chipWidth > containerWidth) {
          scrollPosition = chipsListRef.current.scrollLeft + (chipLeft + chipWidth - containerWidth);
        }

        scrollPosition = Math.max(0, scrollPosition);

        chipsListRef.current.scrollTo({
          left: scrollPosition,
          behavior: "smooth",
        });

        setTimeout(() => setChipsListScrollLeft(scrollPosition), SCROLL_TIMEOUT / 2);
      }
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollInitiatedByClick) return;

      let closestSection = null;
      let closestDistance = Infinity;
      const chipsHeight = chipsRef.current?.getBoundingClientRect().height || 0;

      sectionRefs.current.forEach((ref, id) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const distance = Math.abs(rect.top - chipsHeight);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestSection = id;
          }
        }
      });

      if (closestSection && closestSection !== activeChip) {
        setActiveChip(closestSection);
        scrollToChip(closestSection);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeChip, scrollInitiatedByClick, scrollToChip]);

  return (
    <ChipsContext.Provider
      value={{
        activeChip,
        handleChipClick,
        registerSection,
        chipsListRef,
        chipsRef,
        chipsListScrollLeft,
        setChipsListScrollLeft,
      }}
    >
      {children}
    </ChipsContext.Provider>
  );
};

const ChipsList = ({ children }) => {
  const [isSticky, setIsSticky] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const { chipsListRef, chipsRef, chipsListScrollLeft, setChipsListScrollLeft } = useContext(ChipsContext);
  const { _t } = useTranslate();

  useEffect(() => {
    const handleStickyOnScroll = () => {
      if (chipsRef.current) {
        setIsSticky(chipsRef.current.getBoundingClientRect().top === 0);
      }
    };

    const updateWidths = () => {
      if (chipsListRef.current) {
        const chipsWidth = chipsListRef.current.scrollWidth;
        const containerWidth = chipsListRef.current.clientWidth;
        setShowButtons(chipsWidth > containerWidth);
      }
    };

    window.addEventListener("scroll", handleStickyOnScroll, { passive: true });
    window.addEventListener("resize", updateWidths);

    updateWidths();

    return () => {
      window.removeEventListener("scroll", handleStickyOnScroll);
      window.removeEventListener("resize", updateWidths);
    };
  }, [chipsRef, chipsListRef]);

  const scrollChips = (direction) => {
    if (chipsListRef.current) {
      chipsListRef.current.scrollBy({
        left: direction * SCROLL_BY_AMOUNT,
        behavior: "smooth",
      });

      setTimeout(() => setChipsListScrollLeft(chipsListRef.current.scrollLeft), SCROLL_TIMEOUT / 2);
    }
  };

  return (
    <StyledChipsList ref={chipsRef} $isSticky={isSticky}>
      <ChipsScrollable ref={chipsListRef}>{children}</ChipsScrollable>
      {showButtons && (
        <StyledChipsButtons gap="none">
          <Button
            size="icon"
            variant="transparent"
            icon={<Icon.ChevronLeft />}
            aria-label={_t("Scroll left")}
            onClick={() => scrollChips(-1)}
            disabled={chipsListScrollLeft <= 0}
          />
          <Button
            size="icon"
            variant="transparent"
            icon={<Icon.ChevronRight />}
            aria-label={_t("Scroll right")}
            onClick={() => scrollChips(1)}
            disabled={chipsListScrollLeft >= chipsListRef.current?.scrollWidth - chipsListRef.current?.clientWidth}
          />
        </StyledChipsButtons>
      )}
    </StyledChipsList>
  );
};

const ChipsItem = ({ id, title }) => {
  const { activeChip, handleChipClick } = useContext(ChipsContext);
  const isActive = activeChip === id;
  const handleClick = useCallback(() => handleChipClick(id), [handleChipClick, id]);
  const a11yProps = useClickableA11y({ onClick: handleClick, selected: isActive });

  return (
    <StyledChipsItem $active={isActive} onClick={handleClick} data-chip-id={id} {...a11yProps}>
      {title}
    </StyledChipsItem>
  );
};

const ChipsSection = ({ id, title, children }) => {
  const { registerSection } = useContext(ChipsContext);
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      registerSection(id, ref);
    }
  }, [id, registerSection]);

  return (
    <StyledChipsSection ref={ref}>
      <Text color="dark" weight="bold">
        {title}
      </Text>
      {children}
    </StyledChipsSection>
  );
};

export { Chips, ChipsList, ChipsItem, ChipsSection };
